import { derived, get, writable, type Writable } from "svelte/store";

type Group<V> = {
	selected: Writable<V>;
	details: Writable<{
		type: "remove" | "add" | null;
		value: any;
	}>;
};

const groups = writable<Map<string, Group<any>>>(new Map());

function createGroup<V, M = boolean>(multiple?: M): Group<V | null> {
	let selected = writable<V | Array<V> | null>(multiple ? ([] as V[]) : null);

	let details = writable<{
		type: "remove" | "add" | null;
		value: any;
	}>({
		type: null,
		value: null
	});
	return { selected: selected as Writable<V>, details };
}

export const useGroup = <V, M extends boolean = false>(
	groupId: string,
	value?: M extends true ? Array<V> : V,
	options?: {
		multiple?: M;
	}
) => {
	const group = get(groups).get(groupId);
	if (!group) {
		groups.update((currentGroups) => {
			currentGroups.set(groupId, createGroup<V>(options?.multiple));
			return currentGroups;
		});
	}

	//typecase
	const theGroup = get(groups).get(groupId) as Group<
		M extends true ? Array<V> : V
	>;

	// Helper function subscribes to onChange
	function onChange(
		callback: (
			value: any,
			details: {
				type: "remove" | "add" | null;
				value: any;
			}
		) => void
	) {
		if (!theGroup) {
			return;
		}
		theGroup.selected.subscribe((newValue) => {
			const details = get(theGroup.details);
			if (details.type && details.value) {
				callback?.(newValue, details);
			}
		});
	}

	function setValue(value: any) {
		theGroup.selected.set(value);
	}

	function handleValue() {
		if (!theGroup) {
			return;
		}
		const selectedValue = get(theGroup.selected);
		const details = get(theGroup.details);
		if (options?.multiple) {
			if (Array.isArray(selectedValue)) {
				const index = selectedValue.findIndex((s) => s === value);
				if (index > -1) {
					details.type = "remove";
					details.value = value;
					theGroup.selected.update((newValue) => {
						if (Array.isArray(newValue)) {
							newValue.splice(index, 1);
						}
						return newValue;
					});
				} else {
					details.type = "add";
					details.value = value;
					theGroup.selected.update((newValue) => {
						if (Array.isArray(newValue)) {
							if (value !== undefined) {
								newValue.push(value);
							}
						}
						return newValue;
					});
				}
			}
			return;
		}

		if (get(active)) {
			details.type = "remove";
			details.value = value;
			details.value = theGroup.selected;

			theGroup.selected.set(null as M extends true ? V[] : V);
		} else {
			details.type = "add";
			details.value = value;
			if (value !== undefined) {
				theGroup.selected.set(value);
			}
		}
	}

	const active = derived([theGroup.selected], ([$selected]) => {
		if (options?.multiple) {
			return (
				Array.isArray($selected) &&
				value !== undefined &&
				$selected.includes(value)
			);
		} else {
			return $selected === value;
		}
	});

	return { ...theGroup, active, onChange, setValue, handleValue };
};
